import { useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { pendingTour } from '../tours';
import { useTour } from '../hooks/useTour';
import type { MeResponse } from '../lib/types';

// Subtle "What's new" callout for users with a pending tour. Sits between
// the page header and the widget row on Home. Dismissible — once dismissed
// for the session, the tour is still available via the help menu.

type Props = { me: MeResponse | null };

export const WhatsNewBanner = ({ me }: Props) => {
  const [dismissed, setDismissed] = useState(false);
  const { start } = useTour();

  if (!me || dismissed) return null;

  const toursSeen = (me.preferences?.toursSeen as Record<string, string> | undefined) ?? undefined;
  const tour = pendingTour(toursSeen);
  if (!tour) return null;

  return (
    <div
      role="status"
      className="flex items-center gap-3 rounded-lg border border-brand-orange/30 bg-brand-orange/10 px-4 py-2.5 text-sm"
    >
      <Sparkles className="h-4 w-4 shrink-0 text-brand-orange" aria-hidden="true" />
      <span className="flex-1">
        <span className="font-semibold">What's new — {tour.title}.</span>{' '}
        <span className="text-text-secondary">{tour.blurb}</span>
      </span>
      <button
        type="button"
        onClick={() => start(tour)}
        className="rounded-md bg-brand-orange px-3 py-1 text-xs font-semibold text-white hover:bg-brand-orange-dark"
      >
        Show me
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="grid h-7 w-7 place-items-center rounded text-text-secondary hover:bg-bg-secondary"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
